import { useEffect, useState } from 'react';
import './App.css';

const services = [
  {
    title: 'AI-First Products',
    body: 'Strategy, design, and engineering for AI-native web and mobile experiences built on modern stacks.',
    accent: 'Gradient',
  },
  {
    title: 'Full-Stack Delivery',
    body: 'From React to .NET and Python APIs, we build robust systems that scale securely in the cloud.',
    accent: 'Slate',
  },
  {
    title: 'Launch & Iterate',
    body: 'Fast pilots, thoughtful UX, and observability baked in so you can ship, learn, and improve quickly.',
    accent: 'Cyan',
  },
  {
    title: 'Enterprise Platforms',
    body: 'ERP/MRP workflows, integrations, and cloud-native modernization for resilient operations.',
    accent: 'Gradient',
  },
];

const capabilities = [
  'Generative AI UX, copilots, and automation',
  'Modern web: React, Next.js, TypeScript',
  'APIs and platforms with Node.js and .NET',
  'Data + ML with Python, vector DBs, and pipelines',
  'Native-quality mobile with React Native',
  'Cloud architecture, security, and DevOps',
  'ERP/MRP integrations, workflow automation, and reporting',
  'Cloud platforms: AWS, Azure, GCP, Kubernetes',
];

const highlights = [
  {
    label: 'Product velocity',
    desc: 'Weekly increments with clear demos, decisions, and visible progress.',
    points: ['Roadmaps broken into 1–2 week slices', 'Demos and notes every sprint', 'Risks surfaced early'],
  },
  {
    label: 'Design first',
    desc: 'Experience-led delivery that keeps your users at the center.',
    points: ['UX flows before build', 'AI touchpoints validated with users', 'Accessible, responsive patterns'],
  },
  {
    label: 'Founders welcome',
    desc: 'We partner with startups and enterprises to go from idea to launch.',
    points: ['Idea → pilot in weeks', 'Clear estimates and pricing', 'Hands-on founders get a single point of contact'],
  },
  {
    label: 'Quality & observability',
    desc: 'Instrumentation and safeguards from day one.',
    points: ['Automated testing and linting', 'Tracing, logs, and dashboards', 'Rollout plans with feature flags'],
  },
  {
    label: 'Security ready',
    desc: 'Guardrails for enterprise readiness.',
    points: ['SSO, RBAC, audit logs', 'Data protections per tenant', 'Compliance-friendly practices'],
  },
];

const caseStudies = [
  {
    title: 'AI Support Copilot',
    industry: 'SaaS',
    problem: 'Slow ticket resolution across a growing product surface.',
    approach: 'LLM + retrieval over product docs and release notes.',
    outcome: 'Human-in-loop suggestions with compliance guardrails.',
    metric: '↓38% handle time',
  },
  {
    title: 'Predictive Maintenance',
    industry: 'Manufacturing',
    problem: 'Unexpected downtime from sensor noise and late alerts.',
    approach: 'Streaming ingestion, anomaly detection, and dashboards.',
    outcome: 'Early warnings and workflows for field teams.',
    metric: '↑22% uptime',
  },
  {
    title: 'Intelligent Quoting',
    industry: 'Fintech',
    problem: 'Manual underwriting slowed new account onboarding.',
    approach: 'Document parsing, risk scoring, and assisted decisions.',
    outcome: 'Minutes to quotes with auditable decision trails.',
    metric: '3x faster decisions',
  },
];

const process = [
  { step: '01', title: 'Discover', detail: 'Clarify goals, users, constraints, and define the first shippable win.' },
  { step: '02', title: 'Design', detail: 'Map flows, UX, and AI touchpoints; align on success metrics and risks.' },
  { step: '03', title: 'Build', detail: 'Ship in weekly increments with demos, QA, and observability from day one.' },
  { step: '04', title: 'Launch & Learn', detail: 'Release, measure, and iterate quickly with real-world signals.' },
];

const team = [
  { name: 'Maya Patel', role: 'Product & UX', focus: 'AI-first journeys', blurb: 'Shapes vision, flows, and pilots that validate fast.' },
  { name: 'Ethan Lee', role: 'Engineering', focus: 'Full-stack & cloud', blurb: 'Leads delivery across React, Node/.NET, and Kubernetes.' },
  { name: 'Sofia Ramirez', role: 'Data & ML', focus: 'RAG + analytics', blurb: 'Designs data pipelines, vector stores, and evaluations.' },
];

const showTeam = false;

const trustSignals = [
  { title: 'Security & Compliance', detail: 'SSO, RBAC, audit logs, encryption in transit/at rest, SOC2-ready patterns.' },
  { title: 'Quality & Observability', detail: 'Automated testing, tracing, logging, and dashboards from day one.' },
  { title: 'Support & SLAs', detail: 'Clear runbooks, on-call rotations, and response targets that fit your teams.' },
];

const stack = [
  { category: 'AI/ML', items: 'OpenAI®, Azure OpenAI®, Anthropic®, Hugging Face®, vector DBs: Pinecone®, Qdrant' },
  { category: 'Web & Mobile', items: 'React®, Next.js®, TypeScript®, React Native®' },
  { category: 'APIs & Services', items: 'Node.js®, .NET®, Python; REST/GraphQL; event-driven patterns' },
  { category: 'Data & Pipelines', items: 'Postgres®, SQL Server®, Snowflake®; Airflow®; ingestion and ETL/ELT' },
  { category: 'Cloud & DevOps', items: 'AWS®, Azure®, Google Cloud®; Kubernetes®; Terraform®; CI/CD; security baselines' },
  { category: 'ERP/MRP', items: 'Integrations and workflows with SAP®, Oracle®, and custom systems' },
];

const pricing = [
  { name: 'Pilot', duration: '4–6 weeks', fit: 'Validate an AI feature or workflow', details: ['Focused scope', 'Design + build', 'Weekly demos'] },
  { name: 'Product Squad', duration: 'Quarterly', fit: 'Dedicated cross-functional delivery', details: ['Product + design + eng', 'Roadmap ownership', 'SLA-backed support'] },
  { name: 'Platform Care', duration: 'Ongoing', fit: 'Stability, ops, and iteration', details: ['Reliability + security', 'Optimizations', 'Runbooks and on-call'] },
];

function App() {
  const getInitialTheme = () => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website: '' });
  const [formStatus, setFormStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const contactEndpoint =
    (typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_CONTACT_ENDPOINT) ||
    'https://erp.bizbalance.lk/public/contact';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ state: 'loading', message: 'Sending your message...' });

    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, site: 'qubitx' }),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setFormData({ name: '', email: '', message: '', website: '' });
      setFormStatus({ state: 'success', message: 'Thanks! We will reply shortly.' });
    } catch (err) {
      setFormStatus({ state: 'error', message: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <div className="page">
      <div className="bg-grid" aria-hidden="true" />
      <div className="bg-glow glow-one" aria-hidden="true" />
      <div className="bg-glow glow-two" aria-hidden="true" />
      <header className="nav">
        <div className="logo">
          <img src="/qubitx.png" alt="QubitX AI logo" className="logo-img" />
          <span>QubitX AI</span>
        </div>
        <nav className="nav-links">
          <a href="#services">Services</a>
          <a href="#capabilities">Capabilities</a>
          <a href="#contact">Contact</a>
        </nav>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
          aria-label="Toggle color theme"
        >
          {theme === 'dark' ? 'Light' : 'Dark'} mode
        </button>
        <a className="pill" href="#contact">
          Book a discovery call
        </a>
      </header>

      <main className="content">
        <section className="hero">
          <div className="hero-content">
            <div className="eyebrow">AI studio + engineering team</div>
            <h1>
              Build modern products
              <span className="highlight"> with intelligence at the core.</span>
            </h1>
            <p className="lede">
              QubitX AI designs and ships AI-driven web, mobile, and platform experiences.
              We combine bold product thinking with rigorous engineering to launch fast.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#contact">
                Start a project
              </a>
              <a className="button ghost" href="#services">
                Explore services
              </a>
            </div>
            <div className="metrics">
              <div>
                <span className="metric-number">50+</span>
                <span className="metric-label">products launched</span>
              </div>
              <div>
                <span className="metric-number">24/7</span>
                <span className="metric-label">global collaboration</span>
              </div>
              <div>
                <span className="metric-number">multi-stack</span>
                <span className="metric-label">JS, .NET, Python</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div
              className="hero-photo"
              role="img"
              aria-label="Designers and engineers collaborating over screens with dashboards and cloud diagrams"
            />
            <p className="muted small">AI experiences, cloud scale, and ERP/MRP workflows, built together.</p>
          </div>
        </section>

        <section id="services" className="section cards">
          <div className="section-heading">
            <p className="eyebrow">What we do</p>
            <h2>Strategy, design, and full-stack delivery</h2>
            <p className="muted">
              We partner with teams to conceive AI-native products, validate quickly, and ship quality software with
              clear roadmaps.
            </p>
          </div>
          <div className="card-grid">
            {services.map((service) => (
              <article key={service.title} className={`card card-${service.accent.toLowerCase()}`}>
                <div className="card-accent" />
                <h3>{service.title}</h3>
                <p>{service.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="work" className="section case-studies">
          <div className="section-heading">
            <p className="eyebrow">Case studies</p>
            <h2>Recent wins we&apos;ve delivered</h2>
            <p className="muted">Problem → approach → outcome, with measurable results.</p>
          </div>
          <div className="card-grid">
            {caseStudies.map((item) => (
              <article key={item.title} className="card case-card">
                <div className="metric-pill">{item.metric}</div>
                <h3>{item.title}</h3>
                <p className="muted">{item.industry}</p>
                <p>{item.problem}</p>
                <p className="muted">{item.approach}</p>
                <p className="muted">{item.outcome}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="capabilities" className="section capabilities">
          <div className="section-heading">
            <p className="eyebrow">Technology</p>
            <h2>Modern stacks tailored to your roadmap</h2>
            <p className="muted">
              From delightful frontends to resilient backend platforms, we pick the right tools and ship with confidence.
            </p>
          </div>
          <div className="capability-grid">
            {capabilities.map((item) => (
              <div key={item} className="capability">
                <span className="dot" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="section process">
          <div className="section-heading">
            <p className="eyebrow">Process</p>
            <h2>How we deliver</h2>
            <p className="muted">Transparent steps with clear owners and timelines.</p>
          </div>
          <div className="process-grid">
            {process.map((item) => (
              <div key={item.step} className="step-card">
                <div className="step-number">{item.step}</div>
                <div>
                  <h3>{item.title}</h3>
                  <p className="muted">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {showTeam && (
          <section className="section team">
            <div className="section-heading">
              <p className="eyebrow">Team</p>
              <h2>Leadership that ships</h2>
              <p className="muted">Product, engineering, and data leaders who stay close to delivery.</p>
            </div>
            <div className="team-grid">
              {team.map((person) => (
                <div key={person.name} className="card team-card">
                  <div className="avatar">{person.name.slice(0, 1)}</div>
                  <div className="team-meta">
                    <h3>{person.name}</h3>
                    <p className="muted">{person.role} · {person.focus}</p>
                  </div>
                  <p className="muted">{person.blurb}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="section trust">
          <div className="section-heading">
            <p className="eyebrow">Delivery guardrails</p>
            <h2>Built-in trust and reliability</h2>
            <p className="muted">Security, quality, and support practices that scale with you.</p>
          </div>
          <div className="trust-grid">
            {trustSignals.map((item) => (
              <div key={item.title} className="card trust-card">
                <h3>{item.title}</h3>
                <p className="muted">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="section stack">
          <div className="section-heading">
            <p className="eyebrow">Preferred stack</p>
            <h2>Tools that match your roadmap</h2>
            <p className="muted trademark-note">All trademarks are property of their respective owners.</p>
          </div>
          <div className="stack-grid">
            {stack.map((item) => (
              <div key={item.category} className="stack-card">
                <div className="badge subtle">{item.category}</div>
                <p>{item.items}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="section pricing">
          <div className="section-heading">
            <p className="eyebrow">Engagements</p>
            <h2>Clear models that fit your stage</h2>
          </div>
          <div className="pricing-grid">
            {pricing.map((tier) => (
              <div key={tier.name} className="card pricing-card">
                <div className="badge">{tier.name}</div>
                <p className="muted">{tier.fit}</p>
                <h3>{tier.duration}</h3>
                <ul>
                  {tier.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="section highlights">
          <div className="section-heading">
            <p className="eyebrow">How we work</p>
            <h2>Experience-led, outcome-focused</h2>
          </div>
          <div className="highlight-grid">
            {highlights.map((item) => (
              <div key={item.label} className="highlight-card">
                <div className="badge">{item.label}</div>
                <p>{item.desc}</p>
                {item.points && (
                  <ul>
                    {item.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="section contact">
          <div className="contact-inner">
            <div>
              <p className="eyebrow">Ready to build?</p>
              <h2>Let&apos;s design your next release</h2>
              <p className="muted">
                Tell us about your goals, and we&apos;ll propose an approach with clear milestones, pricing, and a
                kickoff date.
              </p>
              <form className="contact-form" onSubmit={handleSubmit}>
                <input
                  className="honeypot"
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} />
                </div>
                <div className="field">
                  <label htmlFor="message">Project details</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>
                <button type="submit" className="button primary wide">
                  Send message
                </button>
                <p className="muted small">Messages are delivered to support@qubitx.co.nz.</p>
                {formStatus.message && (
                  <p className={`form-status ${formStatus.state}`} aria-live="polite">
                    {formStatus.message}
                  </p>
                )}
              </form>
            </div>
            <div className="contact-info">
              <div className="info-block">
                <p className="eyebrow">Contact</p>
                <a className="button ghost wide" href="mailto:support@qubitx.co.nz">
                  support@qubitx.co.nz
                </a>
              </div>
              <div className="info-block">
                <p className="eyebrow">Address</p>
                <p className="muted">No.43, Cranbrook Place, Glendowie, Auckland, 1071.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-col">
            <h4>Contact</h4>
            <a href="mailto:support@qubitx.co.nz">support@qubitx.co.nz</a>
            <p className="muted small">No.43, Cranbrook Place, Glendowie, Auckland, 1071.</p>
          </div>
          <div className="footer-col">
            <h4>Links</h4>
            <a href="#services">Services</a>
            <a href="#capabilities">Capabilities</a>
            <a href="#contact">Contact</a>
          </div>
          <div className="footer-col">
            <h4>Social</h4>
            <a
              href="https://www.linkedin.com/company/qubitx-ai/about/?viewAsMember=true"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
